import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { getCurrentUser } from '@/app/apis';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Label } from '@/app/components/ui/label';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, getByteLength } from '@/app/utils';
import { handleServiceError } from '@/app/utils/client';
import { usersUpdateProfile } from '../apis';
import { UserInfoDialog } from './UserInfoDialog';

const UserBioInput = () => {
  const { userInfo, updateUserInfo } = useStore((state) => ({
    userInfo: state.userInfo,
    updateUserInfo: state.updateUserInfo,
  }));

  const t = useTranslations();
  const [isEdit, setIsEdit] = useState(false);
  const [value, setValue] = useState(userInfo?.biography);
  const [displayError, setDisplayError] = useState<string | boolean>(false);
  const [open, setOpen] = useState(false);

  // 获取用户信息的 useFetch hook
  const { run: getUserInfo } = useFetch(getCurrentUser, {
    manual: true,
    suppressUnauthorizedLoginDialog: true,
    onSuccess: ({ data }) => {
      if (data) {
        // 更新 store 中的用户信息
        updateUserInfo(data);
      }
    },
  });

  const { run: updateUserName } = useFetch(
    () =>
      usersUpdateProfile({
        body: {
          profile: {
            biography: value,
          },
          mask: 'biography',
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          handleServiceError(error, t);
          return;
        }
        if (data) {
          toast({
            title: t('personal.bio.success'),
          });
          getUserInfo();
          setIsEdit(false);
        }
      },
      onError: () => {
        toast({
          title: t('personal.bio.error'),
        });
      },
    },
  );

  const validateBio = (value: string) => {
    if (getByteLength(value) > 100) {
      return t('personal.bio.lengthError');
    }
    // 使用更兼容的正则表达式验证用户名格式 此次不做
    // const bioRegex = /^[a-zA-Z\u4e00-\u9fa5\u0080-\uFFFF0-9_\-'. ]{0,101}$/;
    // if (!bioRegex.test(value)) {
    //   return t('personal.bio.formatError');
    // }
    return false;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    const error = validateBio(newValue);
    setDisplayError(error);
  };

  const handleInputBlur = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    const error = validateBio(newValue);
    setDisplayError(error);
  };

  const handleEdit = () => {
    if (!userInfo?.auditing_info?.biography) {
      setIsEdit(!isEdit);
    } else {
      toast({
        title: t('personal.bio.auditing'),
      });
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <Label className="text-[18px]">{t('personal.bio.label')}</Label>
        <div className="flex cursor-pointer items-center gap-1">
          <button
            disabled={!!displayError}
            type="button"
            className="font-normal text-[16px] text-system-text02 leading-8"
          >
            {isEdit ? (
              <span className={cn(displayError ? 'text-system-white64' : 'text-white')} onClick={() => setOpen(true)}>
                {t('personal.save')}
              </span>
            ) : (
              <span className={cn(!isEdit && 'text-system-blue02')} onClick={() => handleEdit()}>
                {t('personal.edit')}
              </span>
            )}
          </button>
          <CustomTooltip content={t('personal.bio.info')} trigger={'hover'} disableHoverableContent={true}>
            <BaseLineExclamation className="shrink-0 text-[16px] text-system-text05" />
          </CustomTooltip>
        </div>
      </div>
      <div className="w-full">
        <textarea
          disabled={!isEdit}
          className={cn(
            'flex min-h-[100px] w-full resize-none rounded-8 border border-system-white12 bg-system-bg04 px-3 py-2 text-sm text-system-white64 ring-offset-system-bg06 file:border-0 file:bg-transparent file:font-medium file:text-sm placeholder:text-system-text04 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60',
            displayError && 'border-system-error',
            isEdit && 'text-white',
          )}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          value={value}
          maxLength={101}
          rows={4}
        />
        {displayError && <p className="mt-1 text-sm text-system-error">{displayError}</p>}
      </div>
      <UserInfoDialog
        title={t('personal.bio.dialogTitle')}
        description={t('personal.bio.dialogDescription')}
        open={open}
        onChange={() => {
          updateUserName();
        }}
        setOpen={setOpen}
      />
    </>
  );
};

export default UserBioInput;
